import {useId} from '@mantine/hooks';
import {StyleSheet, css} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {
  concentrationUnitTypePredicate,
  containerQuantityUnitTypePredicate,
  unitTypeFormulaMatchesConcentration,
  unitTypeFormulaMatchesContainerQuantity,
} from 'inventory/InventoryUnitUtil';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Checkbox, {CheckboxProps} from 'ui-library/Checkbox';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {UnitTypesQuery} from './__generated/UnitTypesQuery';
import {UpdateUnitTypeParams, updateUnitType} from './UnitDictionaryDAO';
import {isUnitDictionaryManagementEnabledForUser} from './util';

function ConfigCheckbox({checked, disabled, onChange, text}: CheckboxProps & {text: string}): JSX.Element {
  const id = useId();
  return (
    <div className={css(styles.row)}>
      <Checkbox aria-labelledby={id} disabled={disabled} checked={checked} onChange={onChange} />
      <span id={id} className="u-mini-spacing-left">
        {text}
      </span>
    </div>
  );
}

interface Props {
  unitType: UnitTypesQuery['unitTypes'][number];
  refetch: () => void;
}

export default function UnitTypeInventoryConfig({unitType, refetch}: Props): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const [concentrationIsChecked, setConcentrationIsChecked] = useState(
    concentrationUnitTypePredicate(unitType)
  );
  const [containerQuantityIsChecked, setContainerQuantityIsChecked] = useState(
    containerQuantityUnitTypePredicate(unitType)
  );
  useEffect(() => {
    setConcentrationIsChecked(concentrationUnitTypePredicate(unitType));
    setContainerQuantityIsChecked(containerQuantityUnitTypePredicate(unitType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitType.overrideIsConcentration, unitType.overrideIsContainerQuantity]);

  const saveUpdate = async (data: Partial<UpdateUnitTypeParams>): Promise<void> => {
    await updateUnitType({id: unitType.id, ...data});
    notifications.success(`Successfully updated unit type ${unitType.name}`);
    refetch();
  };

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'UnitTypeInventoryConfig', element: 'root'})}
    >
      <div
        className={css(styles.header)}
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
      >
        Inventory-specific settings <BenchlingIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} />
      </div>
      {isExpanded && (
        <div>
          <ConfigCheckbox
            text="Allow as container quantity?"
            disabled={
              !isUnitDictionaryManagementEnabledForUser() ||
              unitTypeFormulaMatchesContainerQuantity(unitType) ||
              (unitType.overrideIsConcentration ?? false) ||
              unitType.archiveData.archived
            }
            checked={containerQuantityIsChecked}
            onChange={(e) => {
              setContainerQuantityIsChecked(e.target.checked);
              saveUpdate({overrideIsContainerQuantity: e.target.checked});
            }}
          />
          <ConfigCheckbox
            text="Allow as concentration?"
            disabled={
              !isUnitDictionaryManagementEnabledForUser() ||
              unitTypeFormulaMatchesConcentration(unitType) ||
              (unitType.overrideIsContainerQuantity ?? false) ||
              unitType.archiveData.archived
            }
            checked={concentrationIsChecked}
            onChange={(e) => {
              setConcentrationIsChecked(e.target.checked);
              saveUpdate({overrideIsConcentration: e.target.checked});
            }}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  header: {
    cursor: 'pointer',
    color: Colors.NEAR_BLACK,
    fontWeight: 'bold',
  },
  container: {
    marginTop: 'auto',
    marginBottom: '10px',
    marginRight: '10px',
    paddingTop: '10px',
  },
  row: {
    display: 'flex',
    marginTop: '5px',
  },
});
